import { useState, type FormEvent, type ReactNode } from 'react';
import { customerViews, type CustomerViewName } from './customer-types';
import { viewRule, viewWord } from './customer-words';
import './customers.css';

/** The most views of their own a staff member keeps. */
export const MAX_OWN_VIEWS = 12;

export interface CustomerViewsProps {
  view: CustomerViewName;
  /** How many companies each view holds under the other filters; absent while the numbers load. */
  counts: Record<string, number> | undefined;
  onView: (view: CustomerViewName) => void;
  /** The staff member's own saved views, by name. */
  own: readonly string[];
  onApply: (name: string) => void;
  onRemove: (name: string) => void;
  /** Keeps the search, filters, order and period on screen under this name. */
  onSave: (name: string) => void;
}

const SAVE_HELP =
  'Keep this search, these filters, the order and the period under a name. Only you see it.';

const countWord = (counts: Record<string, number> | undefined, view: string): string =>
  counts?.[view] === undefined ? '' : ` ${counts[view].toLocaleString('en-US')}`;

/** The ledger's views as tabs with their counts; one `select` on a phone (CSS shows one of the two). */
function LedgerViews({ view, counts, onView }: CustomerViewsProps): ReactNode {
  return (
    <>
      <div className="ad-cu-views" role="group" aria-label="Saved views">
        {customerViews.map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            title={viewRule[v]}
            onClick={() => onView(v)}
          >
            {viewWord[v]}
            <span className="ad-cu-views__count">{countWord(counts, v)}</span>
          </button>
        ))}
      </div>
      <select
        className="ad-cu-views-select"
        aria-label="Saved view"
        value={view}
        onChange={(e) => onView(e.currentTarget.value as CustomerViewName)}
      >
        {customerViews.map((v) => (
          <option key={v} value={v}>
            {viewWord[v]}
            {countWord(counts, v)}
          </option>
        ))}
      </select>
    </>
  );
}

/** "Save view": a name, then the chip appears. */
function SaveView({ own, onSave }: Pick<CustomerViewsProps, 'own' | 'onSave'>): ReactNode {
  const [naming, setNaming] = useState(false);
  const [name, setName] = useState('');
  const full = own.length >= MAX_OWN_VIEWS;
  const submit = (e: FormEvent): void => {
    e.preventDefault();
    if (name.trim()) onSave(name.trim());
    setName('');
    setNaming(false);
  };
  if (!naming) {
    return (
      <button
        type="button"
        className="ad-cu-link"
        disabled={full}
        title={full ? `You keep ${MAX_OWN_VIEWS} views at most; remove one first.` : SAVE_HELP}
        onClick={() => setNaming(true)}
      >
        Save view
      </button>
    );
  }
  return (
    <form className="ad-cu-save" onSubmit={submit}>
      <input
        aria-label="Name this view"
        placeholder="Name this view"
        maxLength={40}
        value={name}
        autoFocus
        onChange={(e) => setName(e.currentTarget.value)}
        onKeyDown={(e) => (e.key === 'Escape' ? setNaming(false) : undefined)}
      />
      <button type="submit" className="ad-cu-link" disabled={!name.trim()}>
        Save
      </button>
    </form>
  );
}

/**
 * The saved views: the ledger's eight, each with how many companies it holds and its rule on hover, and
 * the staff member's own (a named search, filters, order and period, kept in this browser).
 */
export function CustomerViews(props: CustomerViewsProps): ReactNode {
  return (
    <div className="ad-cu-viewbar">
      <LedgerViews {...props} />
      <div className="ad-cu-own" role="group" aria-label="Your views">
        {props.own.map((name) => (
          <span key={name} className="ad-cu-own__chip">
            <button type="button" onClick={() => props.onApply(name)}>
              {name}
            </button>
            <button
              type="button"
              className="ad-cu-own__remove"
              aria-label={`Remove the view ${name}`}
              onClick={() => props.onRemove(name)}
            >
              ×
            </button>
          </span>
        ))}
        <SaveView own={props.own} onSave={props.onSave} />
      </div>
    </div>
  );
}
